import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/base/Seo";
import { mangaList } from "@/mocks/manga";

export default function ArticlePage() {
  const { id } = useParams();
  const manga = mangaList.find((m) => m.id === id);
  const related = manga
    ? mangaList.filter(
        (m) => m.id !== manga.id && m.category === manga.category
      )
    : [];

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="flex min-h-[70vh] flex-col items-center justify-center px-4 pt-16 text-center">
          <h1 className="font-heading text-3xl text-foreground-900">
            未找到该作品
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            你访问的作品可能已下架或链接有误。
          </p>
          <Link
            to="/"
            className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Seo
        title={`${manga.title} - 番木瓜漫画`}
        description={`${manga.title}，${manga.season}${manga.category === "guide" ? "新番导视" : manga.category === "serial" ? "连载新开" : "热门新番"}作品，${manga.tags.join("、")}题材，当前连载至第 ${manga.chapters} 话，${manga.update}。番木瓜漫画为你提供最新漫画上架与追更资讯。`}
        keywords={`番木瓜漫画,${manga.title},番木瓜漫画${manga.tags[0]},番木瓜漫画最新上架`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: manga.title,
            author: { "@type": "Person", name: manga.author },
            keywords: manga.tags.join(","),
            description: manga.synopsis,
          },
        ]}
      />
      <Navbar />
      <main className="pt-16">
        <article>
          {/* header */}
          <header className="bg-foreground-950">
            <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6 md:py-12">
              <nav aria-label="面包屑" className="text-xs text-background-200/70">
                <Link to="/" className="transition hover:text-primary-300">
                  首页
                </Link>
                <span className="mx-2">/</span>
                <Link
                  to={`/${manga.category === "guide" ? "guide" : manga.category === "serial" ? "serial" : "hot"}`}
                  className="transition hover:text-primary-300"
                >
                  {manga.category === "guide"
                    ? "新番导视"
                    : manga.category === "serial"
                    ? "连载新开"
                    : "热门新番"}
                </Link>
                <span className="mx-2">/</span>
                <span className="text-primary-300">{manga.title}</span>
              </nav>

              <div className="mt-6 flex flex-col gap-6 sm:flex-row">
                <div className="shrink-0 overflow-hidden rounded-2xl">
                  <img
                    src={manga.cover}
                    alt={`${manga.title} 作品封面`}
                    className="h-72 w-52 object-cover object-top sm:h-80 sm:w-56"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
                      {manga.category === "guide"
                        ? "新番导视"
                        : manga.category === "serial"
                        ? "连载新开"
                        : "热门新番"}
                    </span>
                    <span className="rounded-full bg-foreground-950/70 px-3 py-1 text-xs text-background-50">
                      {manga.season}
                    </span>
                    <span className="rounded-full bg-secondary-500 px-3 py-1 text-xs font-bold text-background-50">
                      {manga.status}
                    </span>
                  </div>
                  <h1 className="mt-3 font-heading text-3xl text-background-50 md:text-4xl">
                    {manga.title}
                  </h1>
                  <p className="mt-2 text-sm text-background-200/80">
                    作者：{manga.author}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {manga.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded-md bg-background-100/15 px-2.5 py-1 text-xs text-background-100"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 grid max-w-md grid-cols-3 gap-3 rounded-2xl border border-background-100/15 bg-background-50/5 p-4">
                    <div>
                      <p className="text-xs text-background-200/70">评分</p>
                      <p className="mt-1 flex items-center gap-1 font-heading text-xl text-secondary-300">
                        <i className="ri-star-fill text-sm"></i>
                        {manga.rating}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-background-200/70">连载至</p>
                      <p className="mt-1 font-heading text-xl text-background-50">
                        {manga.chapters} 话
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-background-200/70">更新频率</p>
                      <p className="mt-1 text-sm font-medium text-background-50">
                        {manga.update}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* body */}
          <div className="bg-background-50">
            <div className="mx-auto w-full max-w-5xl px-4 py-10 md:px-6 md:py-14">
              <h2 className="font-heading text-2xl text-foreground-950">
                作品简介
              </h2>
              <p className="mt-4 text-base leading-8 text-foreground-700">
                {manga.synopsis}
              </p>
              <p className="mt-4 text-base leading-8 text-foreground-700">
                作为{manga.season}
                的{manga.category === "guide" ? "重点新番" : manga.category === "serial" ? "新开连载" : "高人气作品"}，《
                {manga.title}
                》凭借扎实的故事与独特的视觉风格，迅速赢得了读者的关注。它把「
                {manga.tags.slice(0, 2).join("、")}
                」元素巧妙融合，既保留了爽快的节奏，也埋下了值得反复品味的伏笔，是
                {manga.tags[0]}题材爱好者不容错过的一部作品。
              </p>

              <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  {
                    icon: "ri-quill-pen-line",
                    title: "看点一 · 剧情节奏",
                    text: "开篇即切入主线，情节推进干脆利落，单元与主线交替展开，追更体验一气呵成，不拖泥带水。",
                  },
                  {
                    icon: "ri-palette-line",
                    title: "看点二 · 视觉呈现",
                    text: "画面张力十足，分镜与色彩运用富有辨识度，动作场面与情感戏份都能精准传达氛围与情绪。",
                  },
                  {
                    icon: "ri-character-recognition-line",
                    title: "看点三 · 角色塑造",
                    text: "主角与配角各有弧光，性格鲜明、关系细腻，让读者在追更中不断投入感情，见证角色的成长。",
                  },
                ].map((b) => (
                  <div
                    key={b.title}
                    className="rounded-2xl border border-background-200 bg-background-100/60 p-5"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                      <i className={`${b.icon} text-lg`}></i>
                    </span>
                    <h3 className="mt-3 text-base font-bold text-foreground-900">
                      {b.title}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-foreground-600">
                      {b.text}
                    </p>
                  </div>
                ))}
              </div>

              <h2 className="mt-12 font-heading text-2xl text-foreground-950">
                追更指南
              </h2>
              <div className="mt-4 rounded-2xl border border-background-200 bg-background-100/60 p-5">
                <ul className="space-y-3 text-sm leading-7 text-foreground-700">
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                    作品当前连载至第 {manga.chapters} 话，更新频率为{manga.update}。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                    加入收藏即可在「最新上架每日更新」中第一时间收到新章节提醒。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                    对题材不熟悉？可先浏览本页看点与简介，再决定是否开坑追更。
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* related */}
          {related.length > 0 && (
            <section className="bg-background-100">
              <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
                <h2 className="font-heading text-2xl text-foreground-950">
                  相关推荐
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  同类型作品中你可能还会喜欢：
                </p>
                <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {related.map((m) => (
                    <Link
                      key={m.id}
                      to={`/article/${m.id}`}
                      className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition hover:-translate-y-1 hover:shadow-soft"
                    >
                      <div className="relative aspect-[3/4] w-full overflow-hidden bg-background-200">
                        <img
                          src={m.cover}
                          alt={`${m.title} 相关推荐`}
                          className="h-full w-full object-cover object-top transition duration-300 group-hover:scale-105"
                        />
                        <span className="absolute left-2.5 top-2.5 rounded-full bg-foreground-950/70 px-2 py-0.5 text-xs text-background-50">
                          {m.season}
                        </span>
                      </div>
                      <div className="p-3.5">
                        <h3 className="truncate text-sm font-bold text-foreground-900">
                          {m.title}
                        </h3>
                        <p className="mt-1 flex items-center gap-1 text-xs text-foreground-500">
                          <i className="ri-star-fill text-accent-500"></i>
                          {m.rating} · {m.update}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}